import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Store, Sparkles, Check, ArrowRight, CircleDot } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, inputCls } from "./ui";
import { ImportReview } from "./BulkImport";

// One-flow store onboarding: Add Store -> AI Import -> Review mapping/tags -> Publish.
export default function StoreWizard({ categories, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [store, setStore] = useState({ name: "", website: "", category: "" });
  const [urls, setUrls] = useState("");
  const [catUrl, setCatUrl] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [removed, setRemoved] = useState(new Set());
  const [publishing, setPublishing] = useState(false);

  const catNames = categories.map((c) => c.name);
  const subsFor = (name) => (categories.find((c) => c.name === name)?.subcategories || []);

  const createStore = async () => {
    if (!store.name.trim()) { toast.error("Store name is required"); return; }
    setBusy(true);
    try { await api.post("/admin/stores", { name: store.name.trim(), website: store.website.trim(), category: store.category.trim() }); }
    catch { /* store may already exist — continue */ }
    setBusy(false); setStep(2);
  };

  const runImport = async () => {
    const list = urls.split("\n").map((u) => u.trim()).filter(Boolean);
    if (!list.length) { toast.error("Paste at least one product URL"); return; }
    setBusy(true);
    setRows(list.map((url) => ({ url, status: "pending" })));
    let ok = 0;
    for (let i = 0; i < list.length; i++) {
      setRows((r) => r.map((x, idx) => (idx === i ? { ...x, status: "importing" } : x)));
      try {
        const { data } = await api.post("/admin/ai/import-product", { url: list[i], store: store.name.trim() });
        ok += 1;
        setRows((r) => r.map((x, idx) => (idx === i ? { url: list[i], status: "done", product: data.product, mapping_source: data.mapping_source } : x)));
      } catch (e) {
        setRows((r) => r.map((x, idx) => (idx === i ? { ...x, status: "failed", error: e?.response?.data?.detail || "failed" } : x)));
      }
    }
    setBusy(false);
    if (ok) setStep(3); else toast.error("No products could be imported");
  };

  const imported = rows.filter((r) => r.status === "done");
  const setEdit = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...e[id], [k]: v } }));

  const extractLinks = async () => {
    const cats = catUrl.split(/[\s,]+/).map((u) => u.trim()).filter((u) => u.startsWith("http"));
    if (!cats.length) { toast.error("Paste one or more category/listing page URLs"); return; }
    setExtracting(true);
    try {
      let found = [];
      for (const c of cats) {
        try { const { data } = await api.post("/admin/ai/extract-links", { url: c }); found = found.concat(data.urls || []); } catch { /* skip a bad page */ }
      }
      found = [...new Set(found)];
      if (found.length) { setUrls((u) => (u ? u + "\n" : "") + found.join("\n")); toast.success(`Found ${found.length} product${found.length > 1 ? "s" : ""}`); setCatUrl(""); }
      else toast.message("No product links found on those pages");
    } finally { setExtracting(false); }
  };

  const publishAll = async (bodies) => {
    setPublishing(true);
    try {
      for (const b of bodies) await api.put(`/admin/products/${b.id}`, b);
      toast.success(`${bodies.length} product${bodies.length > 1 ? "s" : ""} published from ${store.name}`);
      onDone?.(); onClose();
    } catch { toast.error("Publish failed — imported items are saved as drafts in Catalog"); setPublishing(false); }
  };

  const steps = ["Store", "AI Import", "Review & Publish"];

  return (
    <Modal title="Onboard a store" onClose={onClose} testid="store-wizard">
      <div className="flex items-center gap-2 mb-5">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <span className={`flex items-center gap-1.5 text-xs font-semibold ${step === i + 1 ? "text-[#0D530E]" : step > i + 1 ? "text-[#2D7A5D]" : "text-[#5C5C5C]"}`}>
              {step > i + 1 ? <Check size={14} /> : <CircleDot size={14} />} {s}
            </span>
            {i < steps.length - 1 && <ArrowRight size={13} className="text-[#5C5C5C]" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-3" data-testid="wizard-step-1">
          <input className={inputCls} placeholder="Store name (e.g. Boots UK)" value={store.name} onChange={(e) => setStore({ ...store, name: e.target.value })} data-testid="wiz-store-name" />
          <input className={inputCls} placeholder="Website (e.g. boots.com)" value={store.website} onChange={(e) => setStore({ ...store, website: e.target.value })} data-testid="wiz-store-website" />
          <input className={inputCls} placeholder="What they sell (e.g. Beauty, Health)" value={store.category} onChange={(e) => setStore({ ...store, category: e.target.value })} />
          <button onClick={createStore} disabled={busy} data-testid="wiz-store-next" className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Store size={16} />} Save store & continue
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3" data-testid="wizard-step-2">
          <p className="text-sm text-[#5C5C5C]">Bulk-import from <b>{store.name}</b>: paste product URLs (one per line), or paste one or more <b>category/listing pages</b> below and let AI pull up to 40 product links from each.</p>
          <div className="flex gap-2">
            <textarea rows={2} className={inputCls} placeholder="Paste category/listing page URL(s) — one per line — to auto-find products" value={catUrl} onChange={(e) => setCatUrl(e.target.value)} data-testid="wiz-caturl" />
            <button onClick={extractLinks} disabled={extracting} data-testid="wiz-extract-btn" className="shrink-0 bg-[#0D530E] hover:bg-[#083b09] text-white text-sm font-semibold px-4 rounded-xl flex items-center gap-1.5 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{extracting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Find</button>
          </div>
          <textarea rows={6} className={inputCls} placeholder={"https://boots.com/product-1\nhttps://boots.com/product-2"} value={urls} onChange={(e) => setUrls(e.target.value)} data-testid="wiz-urls" />
          {rows.length > 0 && (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {r.status === "done" ? <Check size={13} className="text-[#2D7A5D]" /> : r.status === "importing" ? <Loader2 size={13} className="animate-spin text-[#306D29]" /> : r.status === "failed" ? <span className="text-[#B23B3B] font-bold">✕</span> : <CircleDot size={13} className="text-[#5C5C5C]" />}
                  <span className="truncate flex-1 text-[#5C5C5C]">{r.product?.name || r.url}</span>
                  {r.status === "failed" && <span className="text-[#B23B3B] shrink-0">{r.error}</span>}
                </div>
              ))}
            </div>
          )}
          <button onClick={runImport} disabled={busy} data-testid="wiz-import-btn" className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
            {busy ? <><Loader2 size={16} className="animate-spin" /> Importing…</> : <><Sparkles size={16} /> Import with AI</>}
          </button>
        </div>
      )}

      {step === 3 && (
        <div data-testid="wizard-step-3">
          <ImportReview imported={imported} categories={categories} storeLabel={store.name} onRemove={removeItem} onPublish={publishAll} publishing={publishing} />
        </div>
      )}
    </Modal>
  );
}
